import { lazy, Suspense, useState } from 'react';
import { coordinatesOf } from '@mmef/shared/lib';
import type { EventSummary } from '@mmef/shared/api';

// lazy() at module scope, but the import only fires when the component first renders — which
// happens after the button is pressed. Nothing is fetched for visitors who don't want a map.
const VenueMap = lazy(() => import('./VenueMap').then((m) => ({ default: m.VenueMap })));

/**
 * The venue on a map — but only once asked for.
 *
 * <p><b>Why it isn't shown by default.</b> The map code is nearly free here: Leaflet is already
 * code-split and usually cached from the events list. Tiles are the real cost — one view pulls
 * roughly a dozen images, a few hundred kilobytes, from OpenStreetMap's volunteer-run servers.
 * Rendering a map automatically on every event page would send that traffic for every visitor
 * whether they cared or not, and OSM's tile usage policy explicitly asks applications not to.
 *
 * <p>Most people opening an event page want the address or directions rather than a map to pan
 * around. So: address and directions always, tiles on request.
 */
export function EventLocationMap({ event }: { event: EventSummary }) {
  const [shown, setShown] = useState(false);
  const point = coordinatesOf(event.latitude, event.longitude);

  if (!point) return null;

  const [lat, lng] = point;

  return (
    <section className="section" id="getting-there" data-section="Getting there">
      <h2>Getting there</h2>

      <div className="card__actions">
        <button type="button" className="button" onClick={() => setShown((open) => !open)}>
          {shown ? 'Hide map' : 'Show map'}
        </button>

        {/*
          Google Maps URLs: no API key, no quota, free — it's the JavaScript/Static Maps/Directions
          *APIs* that bill, not a link. On a phone this opens the Maps app rather than a browser
          tab, which is what someone heading to a venue actually wants.

          Coordinates rather than the venue name: a name can resolve to the wrong place, and
          sending someone to the wrong address for an evening event is a bad failure.
        */}
        <a
          className="button"
          href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Directions
        </a>
      </div>

      {shown && (
        <Suspense fallback={<div className="map-panel map-panel--loading" />}>
          <VenueMap lat={lat} lng={lng} title={event.venueName ?? event.title} />
        </Suspense>
      )}
    </section>
  );
}
